import React from 'react';
import { Box, Typography, Paper, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { reportingCapacityAPI } from '../services/reportingCapacityApi';
import { reportingCostAPI } from '../services/reportingCostApi';
import ReportingStackedBars from './ReportingStackedBars';
import { TrendChart, hasSpend, usd, useLoad } from '../pages/ReportingCost';
import { num, monthShort, monthLong } from './ReportingUsageParts';

const REGION_LABELS = { 'us-west-2': 'USW2', 'eu-west-2': 'EUW2', 'eu-west-3': 'EUW3', 'ap-southeast-2': 'ASE2' };
const region = (r) => REGION_LABELS[r] || r || '-';
const Spinner = () => <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;

function Section({ title, state, children, empty }) {
  return (
    <Paper sx={{ p: 2, minWidth: 0 }}>
      <Typography variant="subtitle1" gutterBottom>{title}</Typography>
      {state.error ? <Typography variant="body2" color="text.secondary">{state.error}</Typography>
        : !state.data ? <Spinner />
          : empty ? <Typography color="text.secondary">{empty}</Typography> : children}
    </Paper>
  );
}

// The rest of a customer's profile from the earlier Reporting app: database size, volumes, database instances and AWS cost.
// Every part loads on its own, so one that cannot be read (for example cost data not ingested yet) only says so and never blocks the others.
export default function ReportingCustomerInfra({ custCode, dimension = 'last_year' }) {
  const db = useLoad(async () => (await reportingCapacityAPI.dbSizes(custCode, dimension)).data, [custCode, dimension]);
  const vol = useLoad(async () => (await reportingCapacityAPI.volumes(custCode, 'all_time')).data, [custCode]);
  const cost = useLoad(async () => (await reportingCostAPI.customer(custCode, 12)).data, [custCode]);
  const regions = useLoad(async () => (await reportingCostAPI.regionTrend(custCode, 12)).data, [custCode]);

  const dbMonths = db.data?.months || [];
  const dbBars = dbMonths.map((m) => ({ label: monthShort(m.report_date), values: { total_gb: m.total_gb } }));
  const latestDb = dbMonths[dbMonths.length - 1];
  const snaps = vol.data?.snapshots || [];
  const volRegions = [...new Set(snaps.flatMap((s) => s.by_region.map((r) => r.region)))];
  const volBars = snaps.map((s) => ({ label: monthShort(s.snapshot_date), values: Object.fromEntries(s.by_region.map((r) => [r.region, r.size_gb])) }));
  const latestVol = snaps[snaps.length - 1];
  const trend = cost.data?.trend || [];
  const rows = (regions.data || []).filter(hasSpend).sort((a, b) => (a.region || '').localeCompare(b.region || '') || String(b.month).localeCompare(String(a.month)));

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <Section title="Database size (GB)" state={db} empty={dbBars.length ? '' : 'No database size data.'}>
          <ReportingStackedBars bars={dbBars} keys={['total_gb']} labels={{ total_gb: 'Database size' }} unit="GB" ariaLabel="Database size by month" height={200} />
        </Section>
        <Section title="Volume allocation (GB)" state={vol} empty={volBars.length ? '' : 'No volume data.'}>
          <ReportingStackedBars bars={volBars} keys={volRegions} labels={REGION_LABELS} unit="GB" ariaLabel="Volume allocation by region" height={200} />
        </Section>
      </Box>

      {latestVol?.volumes?.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Volumes - {latestVol.snapshot_date}</Typography>
          <TableContainer>
            <Table size="small" aria-label="Volumes">
              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Drive</TableCell><TableCell>Region</TableCell><TableCell align="right">Size (GB)</TableCell><TableCell>State</TableCell></TableRow></TableHead>
              <TableBody>
                {[...latestVol.volumes].sort((a, b) => Number(b.size_gb) - Number(a.size_gb)).map((v, i) => (
                  <TableRow key={v.volume_id || i} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{v.volume_name || '-'}</TableCell>
                    <TableCell>{v.drive_letter || '-'}</TableCell>
                    <TableCell>{region(v.region)}</TableCell>
                    <TableCell align="right">{num(v.size_gb)} GB</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={v.state === 'in-use' ? 'success' : 'default'} label={v.state || '-'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {latestDb?.instances?.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Database instances - {latestDb.report_date}</Typography>
          <TableContainer>
            <Table size="small" aria-label="Database instances">
              <TableHead><TableRow><TableCell>Instance</TableCell><TableCell>Region</TableCell><TableCell align="right">Log (MB)</TableCell><TableCell align="right">Row (MB)</TableCell><TableCell align="right">Total (GB)</TableCell></TableRow></TableHead>
              <TableBody>
                {[...latestDb.instances].sort((a, b) => Number(b.totalsize_gb) - Number(a.totalsize_gb)).map((i) => (
                  <TableRow key={`${i.db_instance}-${i.region}`} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{i.db_instance}</TableCell><TableCell>{region(i.region)}</TableCell>
                    <TableCell align="right">{num(i.logsize_mb)}</TableCell><TableCell align="right">{num(i.rowsize_mb)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>{num(i.totalsize_gb)} GB</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <Section title="Monthly cost (12 months)" state={cost} empty={trend.length ? '' : 'No cost data for this customer.'}>
          <TrendChart data={trend} />
        </Section>
        <Section title="Cost by region - last 12 months" state={regions} empty={rows.length ? '' : 'No cost data.'}>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader aria-label="Cost by region by month">
              <TableHead><TableRow><TableCell>Region</TableCell><TableCell>Month</TableCell><TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell><TableCell align="right">Saving</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.region}-${r.month}`} hover>
                    <TableCell>{region(r.region)}</TableCell><TableCell>{monthLong(r.month)}</TableCell>
                    <TableCell align="right">{usd(r.cost_gross)}</TableCell><TableCell align="right">{usd(r.cost_net)}</TableCell>
                    <TableCell align="right">{usd(r.cost_total_saving)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      </Box>
    </>
  );
}
